import { isValidEncryptionKey } from '@/infra/crypto/core'
import type { KeyRing, StorageMode } from './types'

export const STORAGE_KEY_PREFIX = 'e2e-keyring-' as const

const memoryStore = new Map<string, KeyRing>()

export const saveKeyRing = (keyRing: KeyRing, mode: StorageMode): boolean => {
  memoryStore.set(keyRing.userEmail, keyRing)

  if (mode === 'localStorage') {
    try {
      const storageKey = `${STORAGE_KEY_PREFIX}${keyRing.userEmail}`
      localStorage.setItem(storageKey, JSON.stringify(keyRing))
    } catch {
      return false
    }
  }

  return true
}

export const loadKeyRing = (userEmail: string, mode: StorageMode): KeyRing | null => {
  if (mode === 'memory') {
    return memoryStore.get(userEmail) ?? null
  }

  try {
    const storageKey = `${STORAGE_KEY_PREFIX}${userEmail}`
    const stored = localStorage.getItem(storageKey)
    if (!stored) return null

    const parsed: unknown = JSON.parse(stored)
    if (!isValidKeyRingShape(parsed)) return null

    return parsed
  } catch {
    return null
  }
}

export const clearKeyRing = (userEmail: string): void => {
  memoryStore.delete(userEmail)

  try {
    const storageKey = `${STORAGE_KEY_PREFIX}${userEmail}`
    localStorage.removeItem(storageKey)
  } catch {
    /* ignored */
  }
}

export const exportKeyRingToFile = (keyRing: KeyRing): void => {
  const json = JSON.stringify(keyRing, null, 2)
  const blob = new Blob([json], { type: 'application/json' })
  const url = URL.createObjectURL(blob)

  const anchor = document.createElement('a')
  anchor.href = url
  const now = new Date()
  const timestamp =
    [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, '0'),
      String(now.getDate()).padStart(2, '0'),
    ].join('-') +
    '_' +
    [String(now.getHours()).padStart(2, '0'), String(now.getMinutes()).padStart(2, '0')].join('')
  anchor.download = `keyring-backup-${timestamp}.json`
  anchor.click()

  URL.revokeObjectURL(url)
}

export const importKeyRingFromFile = async (file: File): Promise<KeyRing> => {
  const text = await file.text()
  const parsed: unknown = JSON.parse(text)

  if (!isValidKeyRingShape(parsed)) {
    throw new Error('Invalid key ring file: structure does not match expected format')
  }

  return parsed
}

export const isValidKeyRingShape = (value: unknown): value is KeyRing => {
  if (typeof value !== 'object' || value === null) return false

  const record = value as Record<string, unknown>

  if (typeof record['userEmail'] !== 'string' || record['userEmail'].length === 0) return false
  if (typeof record['updatedAt'] !== 'string') return false
  if (!Array.isArray(record['keys'])) return false

  const keys = record['keys'] as unknown[]
  return keys.every((k) => {
    if (typeof k !== 'object' || k === null) return false
    const keyRecord = k as Record<string, unknown>
    return (
      typeof keyRecord['version'] === 'number' &&
      Number.isInteger(keyRecord['version']) &&
      keyRecord['version'] >= 1 &&
      typeof keyRecord['key'] === 'string' &&
      isValidEncryptionKey(keyRecord['key']) &&
      typeof keyRecord['createdAt'] === 'string'
    )
  })
}
